// ShareBite reservation exclusivity model (Task 4.1).
//
// Pure compare-and-set (CAS) model of the server-side Firestore reservation
// transaction. The real ReservationService runs a Firestore transaction that
// reads a Donation, checks its `status`, and — only if it is exactly
// "Available" — flips it to "Reserved" while recording the reserving NGO and
// the reservation time. Firestore transaction semantics serialize concurrent
// writers, so any set of concurrent reservation requests is equivalent to some
// sequential ordering of compare-and-set attempts against the shared state.
//
// This module captures exactly that semantics as side-effect-free functions so
// the exactly-one-winner guarantee can be verified independently of Firebase:
//   - `attemptReserve` models a single transaction attempt (the CAS step).
//   - `applyReservationSequence` models a serialized execution of an arbitrary
//     ordering of concurrent requests, surfacing the per-request outcomes plus
//     the final state so callers can assert exactly one winner.
//
// Design reference: design.md "Property 2: Reservation exclusivity" and the
// Testing Strategy note that concurrency is tested with a model-based /
// sequential simulation of the Firestore transaction (compare-and-set on
// `status`).
//
// Requirements:
//   11.2 - On success, change Available -> Reserved and record reserving NGO
//          identifier and reservation timestamp.
//   11.3 - At most one NGO holds an active reservation for a Donation at a time.
//   11.4 - A reservation against a non-Available Donation is rejected, leaving
//          status and the existing reservation record unchanged.
//   11.5 - Among concurrent requests for the same Available Donation, exactly
//          one is granted and all others rejected.
//   11.6 - Reservation exclusivity is enforced atomically server-side.
//   26.7 - On a reservation against an Available Donation: set status Reserved,
//          set reservedBy to the requesting User, set reservedAt to the
//          processing time.
//   26.8 - On a reservation against a non-Available Donation: reject and
//          preserve the existing reservedBy, reservedAt, and status values.
//   27.7 - Reservation atomicity is enforced on the backend, not the UI.
/**
 * Model a single reservation transaction attempt (the compare-and-set step).
 *
 * Succeeds if and only if `state.status` is exactly `"Available"`. On success
 * the returned state is `Reserved` with `reservedBy` set to `ngoUid` and
 * `reservedAt` set to `at` (Req 11.2, 26.7). On any other status the attempt is
 * rejected with `"AlreadyReserved"` and the input state is returned unchanged,
 * preserving the existing `status`, `reservedBy`, and `reservedAt`
 * (Req 11.4, 26.8).
 *
 * Pure: never mutates `state`; returns a fresh state object on success and the
 * original `state` reference on rejection.
 *
 * @param state   The Donation's current reservation state.
 * @param ngoUid  uid of the NGO attempting to reserve.
 * @param at      Time the attempt is processed (epoch ms).
 */
export function attemptReserve(state, ngoUid, at) {
    if (state.status !== "Available") {
        return { ok: false, state, error: "AlreadyReserved" };
    }
    return {
        ok: true,
        state: { status: "Reserved", reservedBy: ngoUid, reservedAt: at },
    };
}
/**
 * Apply an arbitrary ordering of reservation requests as a serialized sequence
 * of compare-and-set attempts, modeling how Firestore transactions serialize
 * concurrent writers (Req 11.5, 11.6, 27.7).
 *
 * Each request is evaluated against the current state via {@link attemptReserve};
 * a successful attempt advances the state to `Reserved`, after which every
 * subsequent request observes a non-Available status and is rejected. Because
 * `attemptReserve` only ever succeeds from `Available`, at most one request in
 * any ordering can win, and if the initial state is `Available` exactly one
 * wins (Req 11.3).
 *
 * Pure: does not mutate `initial` or the `requests` array.
 *
 * @param initial  The Donation's reservation state before any request.
 * @param requests The reservation requests, in the order to be processed.
 *                 Any permutation models a different concurrent interleaving.
 */
export function applyReservationSequence(initial, requests) {
    let current = initial;
    const outcomes = [];
    for (const request of requests) {
        const outcome = attemptReserve(current, request.ngoUid, request.at);
        outcomes.push({ request, outcome });
        current = outcome.state;
    }
    return { finalState: current, outcomes };
}
//# sourceMappingURL=reservation.js.map